import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import type { HttpClient, HttpRequestConfig } from "./HttpClient";
import { HttpError } from "./HttpError";

interface NasamRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  _retry?: boolean;
}

// Without a timeout, a request against a dead/bad connection never settles
// at all -- its promise just hangs, so anything gated on it (a mutation's
// `isPending`, a Save button's `disabled`) spins forever instead of ever
// reaching the app's own error handling. 30s is long enough not to fail a
// genuinely slow-but-working request (a big export, a cold cache), short
// enough that a stuck user sees a real error well within a minute.
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export interface AxiosHttpClientOptions {
  readonly baseUrl: string;
  readonly getAccessToken: () => string | null;
  readonly getLanguage: () => string;
  /** Return the replacement token. Concurrent 401s share this call. */
  readonly refreshAccessToken: () => Promise<string>;
  readonly onRefreshFailure?: () => void;
  /** A 403 from the backend means the frontend's own permission check was
   *  stale or wrong (a race, a just-revoked grant) — not a normal error a
   *  screen should render inline. The caller still gets the rejected
   *  promise (its own error handling, e.g. a toast, still runs) — this is
   *  just the hook for also leaving the current page. */
  readonly onForbidden?: () => void;
  readonly axiosInstance?: AxiosInstance;
  /** Defaults to `DEFAULT_REQUEST_TIMEOUT_MS`. Ignored when `axiosInstance`
   *  is supplied -- that instance's own timeout (if any) is left as-is. */
  readonly timeoutMs?: number;
}

/**
 * Axios's own `timeout` only starts counting once the request is actually
 * dispatched to the network -- when the OS reports no network interface at
 * all (not just a slow/unreachable server), Chrome has been observed to
 * hold the request pre-dispatch (nothing shows in the Network tab, no
 * timer runs) until connectivity returns, then silently complete it. A
 * `setTimeout` here runs on the JS event loop regardless of network state,
 * so `abort()` fires on schedule either way and the request's promise is
 * guaranteed to settle. Forwards any caller-supplied signal (e.g. a
 * query's own cancellation) into the same controller, and tears the timer
 * down once the request settles so it doesn't outlive it.
 */
export function withTimeoutSignal(
  timeoutMs: number,
  callerSignal: AbortSignal | undefined,
): { readonly signal: AbortSignal; readonly cleanup: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const forwardAbort = () => controller.abort();
  callerSignal?.addEventListener("abort", forwardAbort, { once: true });
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", forwardAbort);
    },
  };
}

/** Axios already lower-cases response header names; this just narrows to a
 *  plain Record for HttpError, dropping any non-string (e.g. array) values. */
function responseHeaders(error: AxiosError): Record<string, string> | undefined {
  const headers = error.response?.headers;
  if (headers === undefined) return undefined;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (typeof value === "string") result[key.toLowerCase()] = value;
  }
  return result;
}

/** The API's `message` is a single string, or one entry per invalid field. */
function errorMessages(error: AxiosError): readonly string[] | undefined {
  const data = error.response?.data;
  if (typeof data !== "object" || data === null || !("message" in data)) {
    return undefined;
  }

  const message = (data as { message?: unknown }).message;
  if (typeof message === "string" && message.length > 0) return [message];
  if (
    Array.isArray(message) &&
    message.every((entry): entry is string => typeof entry === "string") &&
    message.length > 0
  ) {
    return message;
  }
  return undefined;
}

// Kernel has no i18next instance of its own (this runs inside an axios
// interceptor, outside the React tree, possibly before the app's i18n is
// even initialized) -- the backend's own `message` is already localized via
// the `x-lang` header we send, but this one fallback is ours alone (no
// response ever arrived for the backend to localize anything), so it needs
// its own tiny bilingual lookup instead. Keyed the same as `Language` in
// ../../stores/languageStore, without importing it to keep this file
// dependency-free of the rest of kernel.
const OFFLINE_MESSAGE_EN =
  "Unable to reach the server. Please check your connection and try again.";
const OFFLINE_MESSAGE_BY_LANGUAGE: Record<string, string> = {
  en: OFFLINE_MESSAGE_EN,
  ar: "تعذر الوصول إلى الخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.",
};

export function toHttpError(error: unknown, language?: string): HttpError {
  if (error instanceof HttpError) return error;

  if (axios.isAxiosError(error)) {
    const messages = errorMessages(error);
    const headers = responseHeaders(error);
    // No response ever arrived -- a timeout, an offline/dropped connection,
    // DNS or CORS failure. Axios's own `message` here is a raw technical
    // string ("timeout of 30000ms exceeded", "Network Error"), not
    // something to show a user; `status` stays undefined either way, same
    // as it already does for these cases, so existing `error.status === 404`
    // -style checks elsewhere are unaffected.
    const fallbackMessage =
      error.response === undefined
        ? (OFFLINE_MESSAGE_BY_LANGUAGE[language ?? "en"] ?? OFFLINE_MESSAGE_EN)
        : error.message;
    return new HttpError(messages?.join(" ") ?? fallbackMessage, {
      status: error.response?.status,
      code: error.code,
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      data: error.response?.data,
      cause: error,
      ...(messages !== undefined && { messages }),
      ...(headers !== undefined && { headers }),
    });
  }

  return new HttpError(
    error instanceof Error ? error.message : "Unexpected HTTP client failure.",
    {
      status: undefined,
      code: undefined,
      method: undefined,
      url: undefined,
      data: undefined,
      cause: error,
    },
  );
}

export function createAxiosHttpClient({
  baseUrl,
  getAccessToken,
  getLanguage,
  refreshAccessToken,
  onRefreshFailure,
  onForbidden,
  axiosInstance,
  timeoutMs,
}: AxiosHttpClientOptions): HttpClient {
  // Only set when we own the instance -- an externally-supplied one keeps
  // whatever timeout behavior (or lack of it) its owner already configured.
  const effectiveTimeoutMs =
    axiosInstance === undefined ? (timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS) : undefined;
  const client =
    axiosInstance ??
    axios.create({
      baseURL: baseUrl,
      withCredentials: true,
      ...(effectiveTimeoutMs === undefined ? {} : { timeout: effectiveTimeoutMs }),
    });
  let refreshPromise: Promise<string> | undefined;

  client.interceptors.request.use((request) => {
    const config = request as NasamRequestConfig;
    config.headers.set("x-lang", getLanguage());

    config.headers.set("ngrok-skip-browser-warning", "true");

    const token = getAccessToken();
    if (!config.skipAuth && token !== null) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }

    // Axios detects FormData and supplies its multipart boundary. Manually
    // setting Content-Type here would produce an invalid request.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (cause: unknown) => {
      if (!axios.isAxiosError(cause)) throw toHttpError(cause, getLanguage());

      if (cause.response?.status === 403) onForbidden?.();

      const config = cause.config as NasamRequestConfig | undefined;
      const shouldRefresh =
        cause.response?.status === 401 &&
        config !== undefined &&
        !config.skipAuth &&
        !config._retry;

      if (!shouldRefresh) throw toHttpError(cause, getLanguage());

      config._retry = true;
      refreshPromise ??= refreshAccessToken()
        .catch((refreshError: unknown) => {
          onRefreshFailure?.();
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = undefined;
        });

      try {
        const token = await refreshPromise;
        config.headers.set("Authorization", `Bearer ${token}`);
        return await client.request(config);
      } catch (refreshError) {
        throw toHttpError(refreshError, getLanguage());
      }
    },
  );

  async function request<T>(config: AxiosRequestConfig): Promise<T> {
    if (effectiveTimeoutMs === undefined) {
      try {
        const response = await client.request<T>(config);
        return response.data;
      } catch (error) {
        throw toHttpError(error, getLanguage());
      }
    }

    const { signal, cleanup } = withTimeoutSignal(
      effectiveTimeoutMs,
      config.signal as AbortSignal | undefined,
    );
    try {
      const response = await client.request<T>({ ...config, signal });
      return response.data;
    } catch (error) {
      throw toHttpError(error, getLanguage());
    } finally {
      cleanup();
    }
  }

  function mapConfig(config?: HttpRequestConfig): AxiosRequestConfig {
    if (config === undefined) return {};

    return {
      params: config.params,
      signal: config.signal,
      headers: config.headers,
      responseType: config.responseType,
      skipAuth: config.skipAuth,
      // Only fetch can outlive the page; axios's default XHR can't.
      ...(config.keepalive === true && {
        adapter: "fetch",
        fetchOptions: { keepalive: true },
      }),
    } as AxiosRequestConfig;
  }

  return {
    get: (url, config) => request({ ...mapConfig(config), method: "GET", url }),
    post: (url, data, config) =>
      request({ ...mapConfig(config), method: "POST", url, data }),
    put: (url, data, config) =>
      request({ ...mapConfig(config), method: "PUT", url, data }),
    patch: (url, data, config) =>
      request({ ...mapConfig(config), method: "PATCH", url, data }),
    delete: (url, config) =>
      request({ ...mapConfig(config), method: "DELETE", url }),
  };
}
